import { useRef } from 'preact/hooks';
import { useChatAreaFade } from '../hooks/useChatAreaFade';

interface Props {
  /** The shared `useTTS` channel is actively speaking — streaming auto-read
   *  (Auto-speak), the SSH summary read, or the SessionDetails "Links" read. */
  speaking: boolean;
  onStop: () => void;
  /** When true, renders statically (no absolute positioning / scroll-fade) so the
   *  control can live inline inside the top bar's right action cluster. */
  inline?: boolean;
}

/**
 * Stop-only playback control — a single ⏹ shown ONLY while the shared `useTTS`
 * channel is actively speaking.
 *
 * P4 (#1652) retired the old "play latest" pill: per-message 🔊 (docTtsStore) is
 * now the sole MANUAL read-aloud surface, so a persistent idle play affordance is
 * redundant. The one thing per-message 🔊 cannot stop is an *in-flight* streaming
 * auto-read (it only renders on completed messages and drives a different engine),
 * so this control survives — purely to give that auto-read a one-tap stop.
 *
 * TTS speed moved to Settings (next to the Auto-speak toggle).
 */
export function PlaybackFab({ speaking, onStop, inline = false }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const scrollFaded = useChatAreaFade(wrapRef);

  // Nothing to show unless the shared channel is actively speaking.
  if (!speaking) return null;

  return (
    <div ref={wrapRef} class={`playback-fab${inline ? ' playback-fab--inline' : ''}${!inline && scrollFaded ? ' scroll-faded' : ''}`}>
      <div class="playback-pill active">
        <button
          type="button"
          class="playback-pill__play"
          onClick={onStop}
          title="Stop playback"
          aria-label="Stop playback"
        >
          {'⏹'}
        </button>
      </div>
    </div>
  );
}
